import { type ReactNode, useCallback, useState } from 'react';
import { noop } from 'lodash';
import { Box, Stack } from '@ardoq/layout';
import { PopoverAtPoint, PopoverContent } from '@ardoq/popovers';
import { Text } from '@ardoq/typography';
import type {
  ItemSet,
  OnTooltip,
} from 'tabview/visualizations/aq-blocks/types';

const nonEmptyLabels = (labels: string[]): string[] =>
  labels.map(label => label.trim()).filter(label => label.length > 0);

const LabelsTooltip = ({ labels }: { labels: string[] }): ReactNode => {
  const [primary, ...rest] = labels;
  return (
    <PopoverContent>
      <Box padding="xsmall">
        <Stack gap="xsmall">
          <Text variant="text2Bold">{primary}</Text>
          {rest.map((label, index) => (
            <Text key={`${label}_${index}`} variant="text2" color="textSubtle">
              {label}
            </Text>
          ))}
        </Stack>
      </Box>
    </PopoverContent>
  );
};

/**
 * Tooltip content for the item under the pointer: the reference's labels when a
 * reference is hovered, otherwise the node's labels. Null when there is nothing
 * to show, so the caller can skip opening an empty popover.
 */
export const buildTooltipContent = (items: ItemSet): ReactNode | null => {
  const reference = items.link();
  const node = items.node();
  const labels = nonEmptyLabels(
    reference ? reference.labels : node ? node.labels : []
  );
  if (labels.length === 0) return null;
  return <LabelsTooltip labels={labels} />;
};

type TooltipState = {
  point: { x: number; y: number };
  content: ReactNode;
};

/** An aq-blocks `doTooltip` handler showing the hovered item's labels, and the popover it opens. */
export const useBlocksTooltip = (): {
  doTooltip: NonNullable<OnTooltip>;
  tooltipPopover: ReactNode;
} => {
  const [tooltip, setTooltip] = useState<TooltipState | null>(null);

  // aq-blocks drives show/hide through this callback's return value, so it must
  // keep a stable identity: changing the doTooltip prop makes aq-blocks cancel
  // the open tooltip.
  const doTooltip: NonNullable<OnTooltip> = useCallback(
    (component, items, position) => {
      const content = buildTooltipContent(items);
      const rect = component.canvas?.getBoundingClientRect();
      // aq-blocks reports the pointer in canvas-local pixels (offsetX/Y). Without
      // the canvas rect (e.g. it unmounted during the dwell delay) we can't offset
      // to the client-space point, so bail rather than misplace it.
      if (!content || !rect) return noop;
      setTooltip({
        content,
        point: { x: rect.left + position[0], y: rect.top + position[1] },
      });
      return () => setTooltip(null);
    },
    []
  );

  const tooltipPopover = tooltip && (
    <PopoverAtPoint
      point={tooltip.point}
      isOpen
      onClose={() => setTooltip(null)}
      placement={{ side: 'bottom', overflow: 'center' }}
    >
      {tooltip.content}
    </PopoverAtPoint>
  );

  return { doTooltip, tooltipPopover };
};
